import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { Link, redirect, useLoaderData, useLocation, useNavigate } from "@remix-run/react";
import { useMemo, useState } from "react";
import {
  RiAddLargeLine,
  RiCheckDoubleLine,
  RiDownloadLine,
  RiMailFill,
  RiShoppingCartFill,
  RiUser3Fill,
} from "react-icons/ri";
import type { SitemapArgs } from "remix-sitemap";
import { serverOnly$ } from "vite-env-only/macros";
import type { Product } from "~types/api/product.types";

import alternativeImage from "~/assets/images/fallback/main-image-fallback.png";
import rangeBackgroundImg from "~/assets/images/fallback/range-background-fallback.jpg";
import { AskEmailModal } from "~/components/AskEmailModal";
import { ErrorComponent, NotFoundPage } from "~/components/error-boundary";
import { HtmlParser } from "~/components/HtmlParser";
import { PriceWarningModal } from "~/components/modals/price-warning-modal";
import { NewsletterBlock } from "~/components/newsletter-block";
import { ProductPackagingBadge, ProductPackagingInfo } from "~/components/product-packaging-info";
import { Stock } from "~/components/Stock";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/cards/card";
import { CompatibleRangeCard } from "~/components/ui/cards/compatible-range-card";
import { ImageGalleryCarousel } from "~/components/ui/carousels/image-gallery-carousel";
import { ProductCardCarousel } from "~/components/ui/carousels/product-card-carousel";
import { Counter } from "~/components/ui/forms/counter";
import { Switch } from "~/components/ui/forms/switch";
import { Image } from "~/components/ui/image";
import { PageContainer } from "~/components/ui/layout/page-container";
import { Wrapper } from "~/components/ui/layout/wrapper";
import { PageHeading } from "~/components/ui/page-heading";
import { Heading, Text } from "~/components/ui/typography";
import { ECO_PRODUCT_REF } from "~/config/products";
import { useAddToCart } from "~/hooks/use-add-to-cart";
import { useComparator } from "~/hooks/use-comparator";
import { getBrandBySlug } from "~/models/brand.server";
import { getProductBySlug } from "~/models/product.server";
import { getSession, sessionStorage } from "~/models/session.server";
import { getSitemapProducts } from "~/models/sitemap.server";
import { useRootMatchData } from "~/root";
import {
  getCharacteristicValue,
  getHighlights,
  getTechnicalElements,
} from "~/services/characteristics";
import {
  getProductBarredPrice,
  getProductFinalPrice,
  getProductPackagingInfo,
  getProductPriceSuffix,
  getProductTags,
  getProductWcPrice,
  isProductNotDisplayedOnExtranet,
  isProductPriceVisible,
  isProductQuantityDecimal,
} from "~/services/product";
import { useAnalyticsEvents } from "~/utils/analytics";
import { cn } from "~/utils/classnames";
import { NotFoundInApiError, NotFoundPageError } from "~/utils/errors";
import { handleError } from "~/utils/errors.server";
import { formatCurrency } from "~/utils/formatters";
import { getSeoMeta } from "~/utils/seo";

export const sitemap = serverOnly$(async ({ request }: SitemapArgs) => {
  const products = await getSitemapProducts({ request });
  return products.map((product) => ({
    loc: `/${product.categories?.[0]?.slug}/${product.slug}`,
    exclude:
      !product.brand_id ||
      !product.brand_slug ||
      !product.categories ||
      product.categories.length < 1 ||
      product.deletedAt ||
      product.type_product === 2 ||
      product.reference === ECO_PRODUCT_REF ||
      isProductNotDisplayedOnExtranet(product as unknown as Product),

    // Alternate language versions of the page
    // alternateRefs: [
    //   {
    //     href: `${config.siteUrl}/en/products/${product.slug}`,
    //     absolute: true,
    //     hreflang: "en",
    //   },
    //   {
    //     href: `${config.siteUrl}/es`,
    //     hreflang: "es",
    //   },
    // ],
  }));
});

export const meta: MetaFunction<typeof loader> = (metaArgs) => {
  const data = metaArgs.data;
  if (!data?.product) {
    return [];
  }

  const subheading = getCharacteristicValue(data.product, "sous-titre");
  const subheadingThumbnail = getCharacteristicValue(data.product, "sous-titre-vignette");

  return getSeoMeta(metaArgs, {
    title: `${data.product.title || ""} - ${data.product.categories[0]?.title || ""}`,
    description: `${subheading || subheadingThumbnail || ""}`,
    cardImageUrl: data.product.images[0]?.url || alternativeImage,
  });
};

export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  try {
    const productSlug = params["product"] ?? null;

    const product = await getProductBySlug({
      slug: productSlug,
      request,
    }).catch((error) => {
      if (error instanceof NotFoundInApiError) {
        throw new NotFoundPageError(`Ce produit n'existe pas ou n'est plus disponible.`);
      }
      throw error;
    });

    if (
      !product ||
      !product.brand ||
      !product.categories ||
      product.categories.length < 1 ||
      product.deletedAt ||
      product.typeProduct === 2 ||
      product.reference === ECO_PRODUCT_REF ||
      isProductNotDisplayedOnExtranet(product)
    ) {
      if (product?.categories?.[0]?.slug) {
        return redirect(`/${product.categories[0].slug}`);
      }
      throw new NotFoundPageError(`Ce produit n'existe pas ou n'est plus disponible.`);
    }

    const productBrand = product.brand?.slug
      ? await getBrandBySlug({ slug: product.brand?.slug, request })
      : null;

    const session = await getSession({ request });
    const recentlyViewedProductIds = session.get("recentlyViewedProductIds");
    if (recentlyViewedProductIds && Array.isArray(recentlyViewedProductIds)) {
      const index = recentlyViewedProductIds.indexOf(product.id);
      if (index !== -1) {
        recentlyViewedProductIds.splice(index, 1);
      }
      if (recentlyViewedProductIds.length > 16) {
        recentlyViewedProductIds.pop();
      }
      recentlyViewedProductIds.unshift(product.id);
      session.set("recentlyViewedProductIds", recentlyViewedProductIds);
    } else {
      session.set("recentlyViewedProductIds", [product.id]);
    }

    return json(
      {
        product,
        productBrand,
      },
      {
        headers: {
          "set-cookie": await sessionStorage.commitSession(session),
        },
      }
    );
  } catch (error) {
    return handleError(error, {
      product: null,
      productBrand: null,
    });
  }
};

export default function ProductPage() {
  const rootData = useRootMatchData();
  const { product, productBrand } = useLoaderData<typeof loader>();

  const location = useLocation();
  const navigate = useNavigate();

  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const packagingInfo = getProductPackagingInfo(product);

  const [quantity, setQuantity] = useState(
    packagingInfo?.isOrderableOnlyByPackaging ||
      packagingInfo?.isOrderableOnlyByPackagingWithDiscount
      ? packagingInfo?.packagingQuantity
      : 1
  );

  const {
    cartFetcher,
    isCartFetcherLoading,
    isCartFetcherDone,
    isPriceWarningModalOpen,
    closePriceWarningModal,
  } = useAddToCart(product);

  const {
    isInComparator,
    handleComparatorProductChange,
    isComparatorFetcherLoading,
    isComparatorFetcherFailed,
  } = useComparator({ comparator: rootData?.comparator });

  const productRangeCompatibles = useMemo(
    () =>
      product?.productRangeCompatibles?.reduce(
        (acc, range) => {
          acc[range.range.id] = Object.entries(range)
            .filter(([, value]) => value === "Y")
            .map(([key]) => key);
          return acc;
        },
        {} as Record<number, string[]>
      ),
    [product?.productRangeCompatibles]
  );

  useAnalyticsEvents({
    pathname: location.pathname,
    pageLoadEvents: [
      {
        event: "view_item",
        data: {
          currency: "EUR",
          value: Number(getProductFinalPrice(product, quantity)) || null,
          items: [
            {
              item_id: product?.reference || "",
              item_name: product?.title || "",
              affiliation: "Powr Connect",
              discount:
                Number(
                  product?.promotion && product?.campaignType === "R"
                    ? (product?.discountAmount ??
                        (Number(product?.discountRate) ?? 0) * (Number(product?.price) ?? 0))
                    : null
                ) || null,
              item_brand: product?.brand?.title || "",
              item_category: product?.categories[0]?.title || "",
              price: Number(getProductFinalPrice(product, quantity)) || null,
            },
          ],
        },
      },
    ],
  });

  if (!product) {
    return null;
  }

  // Breadcrumbs
  const breadcrumbData = [
    {
      name: product?.categories[0]?.title || "",
      slug: product?.categories[0]?.slug ? `/${product.categories[0].slug}` : "",
    },
    {
      name: product?.brand?.title || "",
      slug:
        product?.categories[0]?.slug && product?.brand?.slug
          ? `/${product.categories[0].slug || ""}?marques=${product.brand.slug}`
          : "",
    },
    { name: product?.title || "", slug: "" },
  ];

  const isUserConnected = !!rootData?.contact?.id;

  const subheading = getCharacteristicValue(product, "sous-titre");
  const subheadingThumbnail = getCharacteristicValue(product, "sous-titre-vignette");
  const manufacturerDescription = getCharacteristicValue(product, "description-fabricant");
  const guarantees = getCharacteristicValue(product, "garanties")
    ?.split("/")
    .map((g) => g.trim());
  const technicalElements = getTechnicalElements(product);
  const highlights = getHighlights(product);

  const tags = getProductTags(product);

  const PriceBlock = ({ className }: { className?: string }) => (
    <Card className={cn("px-7 py-5", className)}>
      {isUserConnected ? (
        isProductPriceVisible({ product, contact: rootData?.contact }) ? (
          <>
            {getProductBarredPrice(product, quantity) ? (
              <Text as="p" muted className="relative mb-1 w-max">
                {formatCurrency(getProductBarredPrice(product, quantity))}{" "}
                <span className="absolute -left-[5%] top-1/2 block h-[1px] w-[110%] -rotate-12 bg-grey-700/50" />
              </Text>
            ) : null}

            <div className="flex flex-wrap items-end justify-start gap-4">
              <Text as="p" size="2xl" bold className="leading-none">
                {formatCurrency(getProductFinalPrice(product, quantity))}{" "}
                <Text as="span" size="sm" bold={false} muted>
                  HT{getProductPriceSuffix(product)}
                </Text>
              </Text>
            </div>

            <Text size="sm" muted className="mt-1">
              Hors frais de livraison
            </Text>

            {getProductWcPrice(product, quantity) ? (
              <Text as="p" className="mt-1">
                Soit {formatCurrency(getProductWcPrice(product, quantity), 3)} HT / Wc
              </Text>
            ) : null}

            <ProductPackagingInfo product={product} quantity={quantity} className="mt-4" />

            <cartFetcher.Form
              className="mt-4 flex flex-wrap gap-2"
              method="post"
              action="/cart/actions?/addItem"
            >
              <input type="hidden" name="productId" value={product.id} />
              <Counter
                id={`${product.id}-quantity`}
                name="quantity"
                value={quantity}
                onChange={(e) => setQuantity(Number(e.currentTarget.value))}
                step={
                  packagingInfo?.isOrderableOnlyByPackaging ||
                  packagingInfo?.isOrderableOnlyByPackagingWithDiscount
                    ? packagingInfo?.packagingQuantity
                    : isProductQuantityDecimal(product)
                      ? 0.001
                      : 1
                }
                min={
                  packagingInfo?.isOrderableOnlyByPackaging ||
                  packagingInfo?.isOrderableOnlyByPackagingWithDiscount
                    ? packagingInfo?.packagingQuantity
                    : isProductQuantityDecimal(product)
                      ? 0.001
                      : 1
                }
                max={9999}
                className="grow"
                aria-label="Quantité de produit à ajouter au panier"
              />
              <Button
                type="submit"
                loadingState={
                  isCartFetcherLoading ? "loading" : isCartFetcherDone ? "success" : "idle"
                }
                successText="Ajouté au panier"
                icon={<RiShoppingCartFill />}
                className="grow"
              >
                Ajouter au panier
              </Button>
            </cartFetcher.Form>
          </>
        ) : (
          <>
            <Text as="p" bold>
              Veuillez nous contacter pour commander ce produit
            </Text>
            <Button
              onClick={() =>
                navigate(`/nous-contacter?subject=estimate&product-slug=${product.slug}`)
              }
              icon={<RiMailFill />}
              className="mt-4 flex w-full"
            >
              Nous contacter
            </Button>
          </>
        )
      ) : (
        <>
          <Text as="p" bold>
            Connectez-vous pour accéder aux prix, aux stocks et aux documents de l&apos;ensemble des
            articles.
          </Text>
          <Button
            onClick={() => navigate(`/connexion?redirect=${location.pathname}`)}
            icon={<RiUser3Fill />}
            className="mt-4 flex w-full"
          >
            Se connecter
          </Button>
        </>
      )}
    </Card>
  );

  return (
    <>
      <AskEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        productId={product.id}
        downloadUrl={downloadUrl || ""}
      />
      <PriceWarningModal
        open={isPriceWarningModalOpen}
        setOpen={closePriceWarningModal}
        product={product}
      />
      <PageContainer>
        <PageHeading breadcrumbData={breadcrumbData} withBackButton />
        <Wrapper>
          <section className="grid grid-cols-1 gap-x-16 gap-y-8 md:grid-cols-12">
            <ImageGalleryCarousel
              images={product.images.map((image) => image.url)}
              alt={product.title || ""}
              className="md:col-span-12 xl:col-span-4"
            />

            {/* Product infos */}
            <div className="md:col-span-6 xl:col-span-4">
              {tags && tags.length > 0 ? (
                <div className="mb-3 flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <Badge key={tag.id} color={tag.color}>
                      {tag.label}
                    </Badge>
                  ))}
                </div>
              ) : null}

              <Heading as="h1" size="2xl">
                {product.title}
              </Heading>

              <Text size="xs" muted className="mt-2">
                Réf. POwR : {product.reference}
              </Text>

              {subheading || subheadingThumbnail ? (
                <Text className="mt-4">{subheading || subheadingThumbnail}</Text>
              ) : null}

              {guarantees && guarantees.length > 0 ? (
                <ul className="mt-4 space-y-2">
                  {guarantees.map((guarantee, index) => (
                    <li
                      key={index}
                      className="flex max-w-max items-center gap-3 rounded-full border border-primary-500/5 bg-primary-100 px-3 py-0.5 text-primary-800"
                    >
                      <RiCheckDoubleLine className="size-5 text-primary-500" />
                      <Text size="sm">{guarantee}</Text>
                    </li>
                  ))}
                </ul>
              ) : null}

              {technicalElements && technicalElements.length > 0 ? (
                <>
                  <Heading as="h2" size="lg" className="mt-6">
                    Caractéristiques techniques
                  </Heading>
                  <ul className="bulleted-list mt-2 text-sm font-normal">
                    {technicalElements
                      ? technicalElements.map((technical) => (
                          <li key={technical.slug}>
                            {technical.link ? (
                              <Link
                                className="font-medium underline hover:!text-grey-500"
                                to={technical.link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {technical.value}
                              </Link>
                            ) : (
                              technical.value
                            )}
                          </li>
                        ))
                      : null}
                  </ul>
                </>
              ) : null}

              {isUserConnected ? (
                <ProductPackagingBadge product={product} quantity={quantity} className="mt-6" />
              ) : null}

              {isUserConnected ? (
                <Stock stock={product.stocks[0]} product={product} className="mt-6" />
              ) : null}

              {isUserConnected ? (
                <div className="flex items-center justify-between gap-3 border-y border-grey-200 py-2">
                  <label htmlFor="comparator-switch" className="block text-xs">
                    Comparer ce produit
                  </label>
                  <Switch
                    key={isComparatorFetcherFailed?.toString()}
                    id="comparator-switch"
                    label="Comparer ce produit"
                    disabled={!!isComparatorFetcherLoading}
                    defaultChecked={isInComparator(product.id) || false}
                    onChange={(checked) => {
                      handleComparatorProductChange({
                        id: product.id,
                        categorySlug: product.categories[0]?.slug || "",
                        checked,
                      });
                    }}
                  />
                </div>
              ) : null}
            </div>

            {/* Desktop price block */}
            <div className="md:col-span-6 xl:col-span-4">
              <PriceBlock className="sticky top-[calc(var(--header-height)+1rem)] hidden md:block" />
            </div>
          </section>

          {product.crossProducts && product.crossProducts.length > 0 ? (
            <section>
              <Heading as="h2" className="mt-16">
                Ça s&apos;accorde bien avec
              </Heading>
              <Text as="p" muted className="mt-2">
                Ces articles sont souvent associés avec {product.title} :
              </Text>
              <ProductCardCarousel
                products={product.crossProducts}
                comparator={rootData?.comparator || null}
                contact={rootData?.contact || null}
                className="mt-4"
              />
            </section>
          ) : null}

          <div className="mt-16 grid grid-cols-1 gap-16 md:grid-cols-2">
            <div className="grid gap-16">
              {highlights && highlights.length > 0 ? (
                <section>
                  <Heading as="h2">Les points forts</Heading>
                  <ul className="bulleted-list mt-4">
                    {highlights.map((highlight) => (
                      <li key={highlight.slug}>{highlight.value}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {manufacturerDescription ? (
                <section>
                  <Heading as="h2">Description</Heading>
                  <HtmlParser html={manufacturerDescription} className="mt-4" />
                </section>
              ) : null}
            </div>

            <div className="grid gap-16">
              {product.documents && product.documents?.length > 0 ? (
                <section>
                  <Heading as="h2">Documents à télécharger</Heading>
                  <ul className="mt-4 space-y-2">
                    {product.documents.map((document) => (
                      <li
                        key={document.id}
                        className="flex items-start gap-3 text-sm hover:underline"
                      >
                        <RiDownloadLine className="mt-0.5 size-4 shrink-0" />
                        {isUserConnected ? (
                          <Link
                            to={document.url.replace("http://", "https://")}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {document.title}
                          </Link>
                        ) : (
                          <button
                            onClick={() => {
                              setDownloadUrl(document.url.replace("http://", "https://"));
                              setIsEmailModalOpen(true);
                            }}
                          >
                            {document.title}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}

              <section>
                <Heading as="h2">Une question sur ce produit ?</Heading>
                <Button
                  as="link"
                  to={`/nous-contacter?subject=informations&product-slug=${product.slug}`}
                  icon={<RiMailFill />}
                  className="mt-4"
                >
                  Contactez-nous
                </Button>
              </section>
            </div>
          </div>

          {productBrand?.title ? (
            <section>
              <Card className="mx-auto mt-16 grid max-w-6xl grid-cols-1 items-center gap-8 border-none bg-grey-200 p-8 shadow-none md:grid-cols-12">
                <Image
                  src={productBrand.images[0]?.url}
                  alt={productBrand.title || ""}
                  className="col-span-1 aspect-square max-h-48 w-full rounded-lg bg-white object-contain p-2 md:col-span-3"
                />
                <div className="col-span-1 md:col-span-9">
                  <Heading as="h2" size="2xl">
                    {productBrand.title}
                  </Heading>
                  <HtmlParser html={productBrand.description || ""} className="mt-2" />
                  {productBrand.presentation && productBrand.presentation.length > 0 ? (
                    <Button
                      as="link"
                      to={`/nos-partenaires/${productBrand.slug}`}
                      icon={<RiAddLargeLine />}
                      iconPosition="right"
                      className="mt-4"
                    >
                      En savoir plus
                    </Button>
                  ) : null}
                </div>
              </Card>
            </section>
          ) : null}

          {product.productRangeCompatibles && product.productRangeCompatibles.length > 0 ? (
            <section className="mt-16">
              <Heading as="h2">Module certifié avec les systèmes de fixation</Heading>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {product.productRangeCompatibles
                  .map((range) => range.range)
                  .map((range) => (
                    <CompatibleRangeCard
                      key={`${range.id}-${range.slug}`}
                      title={range.brandRange.title}
                      brandName={range.brand.title}
                      brandImage={range.brand.images[0]?.url || ""}
                      backgroundImage={range.illustrationUrl || rangeBackgroundImg}
                      supportTypes={productRangeCompatibles?.[range.id]}
                      slug={range.slug}
                    />
                  ))}
              </div>
            </section>
          ) : null}

          {product.productRange && product.productRange.length > 0 ? (
            <section className="mt-16">
              <Heading as="h2">Retrouvez cet article dans les gammes</Heading>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {product.productRange
                  .map((range) => range.range)
                  .map((range) => (
                    <CompatibleRangeCard
                      key={`${range.id}-${range.slug}`}
                      title={range.brandRange.title}
                      brandName={range.brand.title}
                      brandImage={range.brand.images[0]?.url || ""}
                      backgroundImage={range.illustrationUrl || rangeBackgroundImg}
                      supportTypes={productRangeCompatibles?.[range.id]}
                      slug={range.slug}
                    />
                  ))}
              </div>
            </section>
          ) : null}

          <NewsletterBlock className="mt-16" />
        </Wrapper>
      </PageContainer>

      {/* Mobile sticky price block */}
      <div className="sticky bottom-0 left-0 right-0 z-10 md:hidden">
        <PriceBlock className="rounded-none shadow-none" />
      </div>
    </>
  );
}

export const ErrorBoundary = () => {
  return (
    <ErrorComponent
      statusHandlers={{
        404: ({ error }) => <NotFoundPage>{error.data}</NotFoundPage>,
      }}
    />
  );
};
